import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, orderBy, updateDoc, doc, deleteDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { useLayoutContext } from '../../components/layout/Layout';
import { getMakecodeArcadePageRootStyle } from './makecodeArcadePageStyles';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import PlusIcon from '../../components/icons/PlusIcon';
import CodeIcon from '../../components/icons/CodeIcon';
import ContentCopyIcon from '../../components/icons/ContentCopyIcon';
import FavoriteIcon from '../../components/icons/FavoriteIcon';
import TrashIcon from '../../components/icons/TrashIcon';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getImgTemplateSize } from '../../utils/makecodeArcadeImg';

interface Sprite {
  id: string;
  name: string;
  code: string;
  authorId: string;
  authorName: string;
  createdAt: Timestamp;
  likes: string[];
  previewUrl: string;
  width?: number;
  height?: number;
  genre: string;
}

const genres = [
  { value: 'fantasy', label: '판타지' }, { value: 'horror', label: '공포' }, { value: 'medieval', label: '중세' },
  { value: 'scifi', label: 'SF' }, { value: 'modern', label: '현대' }, { value: 'cute', label: '귀여운' },
  { value: 'monster', label: '몬스터' }, { value: 'robot', label: '로봇' }, { value: 'nature', label: '자연' },
  { value: 'food', label: '음식' }, { value: 'other', label: '기타' }
];

export const ImageGallery: React.FC = () => {
  const [sprites, setSprites] = useState<Sprite[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [spriteToDelete, setSpriteToDelete] = useState<string | null>(null);
  const [selectedGenre, setSelectedGenre] = useState<string>('');
  const [sortBy, setSortBy] = useState<'date' | 'likes'>('date');
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { isMobile } = useLayoutContext();

  const fetchSprites = async () => {
    try {
      const q = query(collection(db, 'sprites'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const list = snapshot.docs.map(docSnap => {
        const data = docSnap.data();
        let width = 0, height = 0;
        const imgMatch = data.code?.match(/img`([\s\S]*?)`/);
        if (imgMatch) {
          const size = getImgTemplateSize(imgMatch[1]);
          width = size.width;
          height = size.height;
        }
        return { id: docSnap.id, ...data, width, height } as Sprite;
      });
      setSprites(list);
    } catch (error) {
      console.error('Error fetching sprites:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSprites(); }, []);

  const handleCopyCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success('코드가 클립보드에 복사되었습니다.');
    } catch {
      toast.error('코드 복사 중 오류가 발생했습니다.');
    }
  };

  const handleLikeToggle = async (spriteId: string) => {
    if (!currentUser) return;
    const sprite = sprites.find(s => s.id === spriteId);
    if (!sprite) return;
    const newLikes = sprite.likes.includes(currentUser.uid) ? sprite.likes.filter(id => id !== currentUser.uid) : [...sprite.likes, currentUser.uid];
    await updateDoc(doc(db, 'sprites', spriteId), { likes: newLikes });
    fetchSprites();
  };

  const isSuperAdmin = currentUser?.role === 'superAdmin';
  const spriteToDeleteData = sprites.find(s => s.id === spriteToDelete);
  const isAuthor = spriteToDeleteData?.authorId === currentUser?.uid;

  const handleDeleteConfirm = async () => {
    if (!spriteToDelete) return;
    try {
      await deleteDoc(doc(db, 'sprites', spriteToDelete));
      fetchSprites();
      setDeleteDialogOpen(false);
      setSpriteToDelete(null);
      toast.success('삭제되었습니다.');
    } catch {
      toast.error('스프라이트 삭제 중 오류가 발생했습니다.');
    }
  };

  const filteredAndSortedSprites = (() => {
    let result = selectedGenre ? sprites.filter(s => s.genre === selectedGenre) : sprites;
    result = [...result].sort((a, b) => sortBy === 'date' ? b.createdAt.toMillis() - a.createdAt.toMillis() : b.likes.length - a.likes.length);
    return result;
  })();

  if (loading) {
    return (
      <div style={{ ...getMakecodeArcadePageRootStyle(isMobile), textAlign: 'center' }}>로딩 중...</div>
    );
  }

  return (
    <div style={getMakecodeArcadePageRootStyle(isMobile)}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px', fontFamily: 'var(--font-family-primary)' }}>스프라이트 갤러리</h1>
          <p style={{ margin: '8px 0 0', fontSize: '14px', color: 'var(--Color-text-normal)', lineHeight: 1.5 }}>
            img` 코드로 만든 픽셀 스프라이트를 모아 두고, 코드를 복사해 게임에 사용할 수 있습니다.
          </p>
        </div>
        {currentUser && (
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button variant="primary" leftIcon={<PlusIcon width={20} height={20} />} onClick={() => navigate('/makecodearcade/editor')}>에디터로 등록</Button>
            <Button variant="outline" leftIcon={<CodeIcon width={20} height={20} />} onClick={() => navigate('/makecodearcade/editor/code')}>코드로 등록</Button>
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <span style={{ fontSize: '14px', fontWeight: 600, marginRight: '8px' }}>장르:</span>
          <button type="button" onClick={() => setSelectedGenre('')} style={{ padding: '6px 12px', border: 'none', borderRadius: '8px', background: !selectedGenre ? 'var(--Color-fill-brand-color)' : '#f5f5f5', color: !selectedGenre ? 'white' : '#161616', cursor: 'pointer', fontSize: '14px' }}>전체</button>
          {genres.map(g => <button key={g.value} type="button" onClick={() => setSelectedGenre(g.value)} style={{ padding: '6px 12px', border: 'none', borderRadius: '8px', background: selectedGenre === g.value ? 'var(--Color-fill-brand-color)' : '#f5f5f5', color: selectedGenre === g.value ? 'white' : '#161616', cursor: 'pointer', fontSize: '14px' }}>{g.label}</button>)}
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <span style={{ fontSize: '14px', fontWeight: 600, marginRight: '8px' }}>정렬:</span>
          <button type="button" onClick={() => setSortBy('date')} style={{ padding: '6px 12px', border: 'none', borderRadius: '8px', background: sortBy === 'date' ? 'var(--Color-fill-brand-color)' : '#f5f5f5', color: sortBy === 'date' ? 'white' : '#161616', cursor: 'pointer', fontSize: '14px' }}>최신순</button>
          <button type="button" onClick={() => setSortBy('likes')} style={{ padding: '6px 12px', border: 'none', borderRadius: '8px', background: sortBy === 'likes' ? 'var(--Color-fill-brand-color)' : '#f5f5f5', color: sortBy === 'likes' ? 'white' : '#161616', cursor: 'pointer', fontSize: '14px' }}>인기순</button>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '24px' }}>
        {filteredAndSortedSprites.map(sprite => (
          <Card key={sprite.id} padding="16px" style={{ display: 'flex', flexDirection: 'column', gap: '12px', height: '100%' }}>
            <div style={{ width: '100%', aspectRatio: '1', background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px', overflow: 'hidden' }}>
              <img src={sprite.previewUrl} alt={sprite.name} style={{ maxWidth: '100%', maxHeight: '100%', imageRendering: 'pixelated', transform: 'scale(4)', transformOrigin: 'center' }} />
            </div>
            <div style={{ fontSize: '18px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sprite.name}</div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--gap-level-4)',
                color: '#666',
                fontSize: '14px',
              }}
            >
              <span
                style={{
                  minWidth: 0,
                  flex: '1 1 auto',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {sprite.authorName}
              </span>
              <span style={{ flexShrink: 0, color: 'var(--Color-text-normal)' }}>{sprite.width} × {sprite.height} 픽셀</span>
            </div>
            <div style={{ fontSize: '14px', color: 'var(--Color-text-normal)' }}>{genres.find(g => g.value === sprite.genre)?.label || '기타'}</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto' }}>
              <div style={{ display: 'flex', gap: '4px' }}>
                <button type="button" onClick={() => handleCopyCode(sprite.code)} title="코드 복사" style={{ padding: '8px', border: 'none', background: 'transparent', cursor: 'pointer' }}><ContentCopyIcon width={20} height={20} /></button>
                <button type="button" onClick={() => handleLikeToggle(sprite.id)} title={sprite.likes.includes(currentUser?.uid || '') ? '좋아요 취소' : '좋아요'} style={{ padding: '8px', border: 'none', background: 'transparent', cursor: 'pointer', color: sprite.likes.includes(currentUser?.uid || '') ? '#FF6F0F' : '#999' }}><FavoriteIcon width={20} height={20} fill={sprite.likes.includes(currentUser?.uid || '') ? '#FF6F0F' : '#999'} /></button>
                {((currentUser && sprite.authorId === currentUser.uid) || currentUser?.role === 'superAdmin') && <button type="button" onClick={() => { setSpriteToDelete(sprite.id); setDeleteDialogOpen(true); }} title="삭제" style={{ padding: '8px', border: 'none', background: 'transparent', cursor: 'pointer' }}><TrashIcon width={20} height={20} color="#F44336" /></button>}
              </div>
              <span style={{ fontSize: '14px', color: '#666' }}>{sprite.likes.length}명이 좋아합니다</span>
            </div>
          </Card>
        ))}
      </div>
      {deleteDialogOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300 }} onClick={() => { setDeleteDialogOpen(false); setSpriteToDelete(null); }}>
          <div style={{ background: 'white', padding: '24px', borderRadius: '12px', maxWidth: '400px', width: '90%' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px', fontSize: '18px' }}>스프라이트 삭제</h3>
            <p style={{ margin: '0 0 16px', fontSize: '14px' }}>정말로 이 스프라이트를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.</p>
            {isSuperAdmin && !isAuthor && <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#F57C00', fontStyle: 'italic' }}>슈퍼관리자 권한으로 다른 사용자의 작품을 삭제합니다.</p>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <Button variant="cancel" onClick={() => { setDeleteDialogOpen(false); setSpriteToDelete(null); }}>취소</Button>
              <Button variant="primary" onClick={handleDeleteConfirm} style={{ background: '#F44336' }}>삭제</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
